/* accordion.js — KOSHO SG <details>-based accordion with JS-controlled open/close.
 * ----------------------------------------------------------------------------
 * DOM contract:
 *   [data-accordion]                       <- group root (optional attr: data-accordion-single)
 *     [data-accordion-item]                <- each <details>
 *       [data-accordion-summary]           <- <summary> click target
 *       [data-accordion-body]              <- collapsible content wrapper
 *
 * Behaviour:
 *   - Hijacks native <details> toggling (preventDefault on summary click) and
 *     animates the body height between 0 and scrollHeight (300ms ease-out).
 *   - Sets aria-expanded on the summary; toggles the `open` attribute on the item.
 *   - Multiple items may be open at once by default; data-accordion-single on the
 *     root limits to one-open-at-a-time.
 *   - Respects prefers-reduced-motion (immediate toggle, no transition).
 *
 * Exposes window.KoshoAccordion = { mountAll(), Accordion }.
 * ------------------------------------------------------------------------- */

(function (window, document) {
  'use strict';

  var OPEN_MS  = 320;
  var CLOSE_MS = 280;
  var EASE     = 'cubic-bezier(0.4, 0, 0.2, 1)';   // smooth standard easing (Material)
  var prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function Accordion(root) {
    if (!root || root.__koshoAccordion) return;
    this.root    = root;
    this.single  = root.hasAttribute('data-accordion-single');
    this.items   = Array.prototype.slice.call(root.querySelectorAll('[data-accordion-item]'));
    this.mounted = false;
    this._bind();
    root.__koshoAccordion = this;
    this.mounted = true;
  }

  Accordion.prototype._bind = function () {
    var self = this;
    this.items.forEach(function (item) {
      var summary = item.querySelector('[data-accordion-summary]');
      var body    = item.querySelector('[data-accordion-body]');
      if (!summary || !body) return;

      // Initial ARIA + body height sync.
      summary.setAttribute('aria-expanded', item.hasAttribute('open') ? 'true' : 'false');
      body.style.overflow = 'hidden';
      body.style.height = item.hasAttribute('open') ? 'auto' : '0px';

      summary.addEventListener('click', function (e) {
        e.preventDefault();
        try { self.toggle(item); } catch (err) {}
      });
      // Keyboard: Enter/Space already trigger <summary> click natively.
    });
  };

  Accordion.prototype.toggle = function (item) {
    if (!item) return;
    var willOpen = !item.hasAttribute('open');
    if (willOpen && this.single) this._closeOthers(item);
    if (willOpen) this.open(item); else this.close(item);
  };
  Accordion.prototype.open = function (item) {
    var summary = item.querySelector('[data-accordion-summary]');
    var body    = item.querySelector('[data-accordion-body]');
    if (!body) return;
    if (item.__koshoAccBusy) return;
    item.__koshoAccBusy = true;

    item.setAttribute('open', '');
    if (summary) summary.setAttribute('aria-expanded', 'true');

    if (prefersReducedMotion) {
      body.style.height = 'auto';
      body.style.paddingTop = '';
      body.style.paddingBottom = '';
      item.__koshoAccBusy = false;
      return;
    }

    // Snapshot natural padding (from CSS) BEFORE clearing it.
    var cs = window.getComputedStyle(body);
    var padTop    = cs.paddingTop;
    var padBottom = cs.paddingBottom;

    // Start collapsed: height + padding all zero.
    body.style.height = '0px';
    body.style.paddingTop = '0px';
    body.style.paddingBottom = '0px';
    body.style.opacity = '0';
    void body.offsetHeight;                 // force layout sync

    // Measure target height WITH padding restored (scrollHeight reflects content + padding).
    body.style.paddingTop = padTop;
    body.style.paddingBottom = padBottom;
    var target = body.scrollHeight;
    body.style.paddingTop = '0px';
    body.style.paddingBottom = '0px';
    void body.offsetHeight;

    // Animate height + padding together — eliminates the "padding floor" stutter.
    body.style.transition =
      'height '         + OPEN_MS + 'ms ' + EASE + ', ' +
      'padding-top '    + OPEN_MS + 'ms ' + EASE + ', ' +
      'padding-bottom ' + OPEN_MS + 'ms ' + EASE + ', ' +
      'opacity '        + OPEN_MS + 'ms ' + EASE;

    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        body.style.height = target + 'px';
        body.style.paddingTop = padTop;
        body.style.paddingBottom = padBottom;
        body.style.opacity = '1';
      });
    });

    var onEnd = function (e) {
      if (e.propertyName !== 'height') return;
      body.style.transition = '';
      body.style.height = 'auto';
      body.style.opacity = '';
      body.style.paddingTop = '';        // let CSS reclaim
      body.style.paddingBottom = '';
      item.__koshoAccBusy = false;
      body.removeEventListener('transitionend', onEnd);
    };
    body.addEventListener('transitionend', onEnd);
  };
  Accordion.prototype.close = function (item) {
    var summary = item.querySelector('[data-accordion-summary]');
    var body    = item.querySelector('[data-accordion-body]');
    if (!body) return;
    if (item.__koshoAccBusy) return;
    item.__koshoAccBusy = true;

    if (summary) summary.setAttribute('aria-expanded', 'false');

    if (prefersReducedMotion) {
      item.removeAttribute('open');
      body.style.height = '0px';
      body.style.paddingTop = '0px';
      body.style.paddingBottom = '0px';
      body.style.opacity = '0';
      item.__koshoAccBusy = false;
      return;
    }

    // Lock current paddings (from CSS) so the transition starts from them, not 'auto'.
    var cs = window.getComputedStyle(body);
    body.style.paddingTop    = cs.paddingTop;
    body.style.paddingBottom = cs.paddingBottom;
    body.style.opacity = '1';

    // Fix from auto -> px so the height transition has a starting value.
    body.style.height = body.scrollHeight + 'px';
    body.style.transition =
      'height '         + CLOSE_MS + 'ms ' + EASE + ', ' +
      'padding-top '    + CLOSE_MS + 'ms ' + EASE + ', ' +
      'padding-bottom ' + CLOSE_MS + 'ms ' + EASE + ', ' +
      'opacity '        + CLOSE_MS + 'ms ' + EASE;

    void body.offsetHeight;                 // force layout sync

    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        body.style.height = '0px';
        body.style.paddingTop = '0px';
        body.style.paddingBottom = '0px';
        body.style.opacity = '0';
      });
    });

    var onEnd = function (e) {
      if (e.propertyName !== 'height') return;
      body.style.transition = '';
      // Keep height/padding at 0 inline (body is now in closed state).
      item.removeAttribute('open');
      item.__koshoAccBusy = false;
      body.removeEventListener('transitionend', onEnd);
    };
    body.addEventListener('transitionend', onEnd);
  };

  Accordion.prototype._closeOthers = function (keep) {
    var self = this;
    this.items.forEach(function (it) {
      if (it !== keep && it.hasAttribute('open')) self.close(it);
    });
  };

  /* ------------------------------------------------------------------------
   * Auto-mount
   * ------------------------------------------------------------------------ */
  function mountAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-accordion]');
    var instances = [];
    Array.prototype.forEach.call(roots, function (root) {
      if (root.__koshoAccordion) return;
      try { instances.push(new Accordion(root)); } catch (e) {}
    });
    return instances;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountAll);
  } else {
    mountAll();
  }

  window.KoshoAccordion = { mountAll: mountAll, Accordion: Accordion };

})(window, document);
